/**
 * The page's tracker — a module value, set once at boot (`configureTracking`), read by
 * {@link import("./useTracker").useTracker} and by plain code alike.
 *
 * Until 2026-10-21 it was a React context that `<TrackerProvider ga={…}>` filled, so every app
 * wrapped its tree in a component whose only job was to hold one value for the whole page. A page
 * has one tracker and decides it once, from its build's `VITE_GOOGLE_TAG_ID`; a module says that,
 * and an event fired outside the tree (a router listener, an interceptor) reaches it too.
 */
import type { Tracker } from './Tracker';
import { sendOne } from './sendOne';
import { googleAnalytics } from './adapters/googleAnalytics';

export interface TrackingOptions {
  /** Google Analytics 4 measurement id. Empty/undefined disables GA. */
  ga?: string | null | undefined;
  /**
   * A fully custom {@link Tracker}, activated IN ADDITION to `ga` — for a backend this package does
   * not ship yet.
   */
  tracker?: Tracker | null | undefined;
}

let active: Tracker | null = null;
let configured: { ga: string; tracker: Tracker | null } | undefined;

/**
 * Fan-out wrapper so several backends can be active at once. `track`/`pageView` are dispatched to
 * every configured tracker; `init` runs them all. Returns the single tracker unwrapped when only
 * one is configured, or `null` when none are.
 */
function compose(trackers: Tracker[]): Tracker | null {
  if (trackers.length === 0) return null;
  if (trackers.length === 1) return trackers[0] ?? null;
  return {
    init() {
      trackers.forEach((t) => void t.init());
    },
    track(event) {
      trackers.forEach((t) => t.track(event));
    },
    async send(event) {
      await Promise.all(trackers.map((t) => sendOne(t, event)));
    },
    pageView(path) {
      trackers.forEach((t) => t.pageView?.(path));
    },
  };
}

/**
 * Choose this page's backends and initialize them. Call it once, at module level in `App.tsx`:
 *
 * ```ts
 * configureTracking({ ga: import.meta.env.VITE_GOOGLE_TAG_ID });
 * ```
 *
 * Calling it again with the same options is a no-op (a hot reload, a StrictMode double effect), so
 * GA's script is injected once.
 */
export function configureTracking({ ga, tracker = null }: TrackingOptions = {}): void {
  const id = ga?.trim() ?? '';
  if (configured && configured.ga === id && configured.tracker === tracker) return;
  configured = { ga: id, tracker };
  const trackers: Tracker[] = [];
  if (id) trackers.push(googleAnalytics(id));
  if (tracker) trackers.push(tracker);
  active = compose(trackers);
  if (active && typeof window !== 'undefined') void active.init();
}

/** The configured tracker, or `null` when tracking is off (every call is then a no-op). */
export function activeTracker(): Tracker | null {
  return active;
}
